import { currencyExponentExceptions, defaultCurrencyExponent } from '@constants/currency-exponent-exceptions';
import { currencySymbolCodes } from '@constants/currency-symbol-codes';
import { isNullOrWhitespace } from '@functions/is-null-or-whitespace';
import { parseNumberWithThousandSeparator } from '@functions/number-parser';
import { UserLangUtils } from '@utils/user-lang-utils';

export class AmountUtils {
  private static readonly currencyRegexString = '^\\s*[A-Z]{3,3}\\s*$';
  private static readonly browserLanguages = UserLangUtils.getUserLanguages();

  static normaliseNumber(value: string | number): { isValid: boolean; value?: number } {
    if (typeof value === 'number') {
      const isValid = !isNaN(value) && !isNaN(parseFloat(value.toString()));

      return {
        isValid: isValid,
        value: isValid ? value : undefined,
      };
    }

    if (isNullOrWhitespace(value)) {
      return {
        isValid: false,
      };
    }

    const trimmedValue = value.trim();

    const validCharsRegex = /^[0-9 .,]+$/;

    if (!validCharsRegex.test(trimmedValue)) {
      return {
        isValid: false,
      };
    }

    const normalisedValue = parseNumberWithThousandSeparator(value, null);

    const isValid = normalisedValue != null && !isNaN(normalisedValue);

    return {
      isValid,
      value: isValid ? normalisedValue : undefined,
    };
  }

  static isNumeric(value: string | number): boolean {
    return this.normaliseNumber(value).isValid;
  }

  static convertToAmountWithDecimals(amount: string, currencyCode: string): string {
    let amountAsNumber = parseNumberWithThousandSeparator(amount, this.browserLanguages);

    return this.convertNumberToAmountWithDecimals(amountAsNumber, currencyCode);
  }

  static convertAmountToAmountMinor(amount: string, currencyCode: string): number {
    if (!this.isCurrencyValid(currencyCode)) {
      throw new Error(`Invalid currency: ${currencyCode}`);
    }

    const amountNormalised = AmountUtils.normaliseNumber(amount);

    if (!amountNormalised.isValid) {
      return 0;
    }

    const amountAsNumber = (amountNormalised.value)
      .toFixed(this.getCurrencyExponent(currencyCode));

    return Math.round(+amountAsNumber * this.getCurrencyFactor(currencyCode));
  }

  static convertNumberToAmountWithDecimals(amount: number, currencyCode: string): string {
    const amountNormalised = this.normaliseNumber(amount);
    if (!amountNormalised.isValid) {
      amountNormalised.value = 0;
    }

    return (amountNormalised.value).toFixed(this.getCurrencyExponent(currencyCode));
  }

  static convertAmountMinorToAmount(amountMinor: number, currencyCode: string, withDecimals: boolean = false): string {
    if (!this.isCurrencyValid(currencyCode)) {
      throw new Error(`Invalid currency: ${currencyCode}`);
    }

    if (amountMinor == null) {
      amountMinor = 0;
    }

    if (!this.isNumeric(amountMinor)) {
      throw new Error(`AmountMinor ${amountMinor} is not valid value`);
    }

    const amount = amountMinor / this.getCurrencyFactor(currencyCode);

    return withDecimals ? this.convertNumberToAmountWithDecimals(amount, currencyCode) : amount.toString();
  }

  static convertAmountMinorToAmountWithDecimalsAndThousandsSeparators(amountMinor: number, currencyCode: string): string {
    const amount = this.convertAmountMinorToAmount(amountMinor, currencyCode, true);

    var parts = amount.toString().split('.');

    if (parts.length !== 2) {
      return this.convertNumberToAmountWithDecimals(parseFloat(parts[0]), currencyCode);
    }

    const separators = UserLangUtils.getLocaleSeparators();

    const integerPlace = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separators.thousand);
    const decimalPlace = parts[1];

    return `${integerPlace}${separators.decimal}${decimalPlace}`;
  }

  static isCurrencyValid(currencyCode: string): boolean {
    if (isNullOrWhitespace(currencyCode)) {
      return false;
    }

    return currencyCode.match(this.currencyRegexString) != null;
  }

  static getCurrencyFactor(currencyCode: string): number {
    const currencyExponent = this.getCurrencyExponent(currencyCode);

    return this.int10Pow(currencyExponent);
  }

  static currencyCodeLookup(currencyCode) {
    if (isNullOrWhitespace(currencyCode)) {
      return '';
    }

    const currencySymbol = currencySymbolCodes.get(currencyCode);

    return currencySymbol?.CurrencySymbol ?? currencyCode;
  }

  static getCurrencyExponent(currencyCode: string): number {
    return currencyExponentExceptions.has(currencyCode)
    ? currencyExponentExceptions.get(currencyCode)
    : defaultCurrencyExponent;
  }

  private static int10Pow(exponent: number) {
    return exponent <= 0 ? 1 : 10 * this.int10Pow(exponent - 1);
  }
}
